import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Grid, Paper } from '@material-ui/core'
import connect from './connect'
import SpacingContainerBase from '../../components/layouts/SpacingContainerBase'
import { SpacingContainer } from '../../components/common/Containers'
import Loader from '../../components/common/Loader'
import NothingFoundCard from '../../components/generics/Cards/NothingFoundCard/NothingFoundCardList'
import Typography from '../../components/common/Typography'
import { useTrans } from '../../services/i18n'
import { emptyArray } from '../../utils/arrayUtils'
import Header from './components/Header'
import List from './components/List'
import PaginationFooter from './components/PaginationFooter'
import EventFormModal from './components/EventFormModal'
import CalendarView from './components/calendar/CalendarView'

export const EventManagement = ({
  events,
  onInit,
  onChangeOrder,
  onChangePage,
  onChangeRowPerPage,
  deleteEvent,
  duplicateEvent,
  createEvent,
  updateEvent,
  refetchCalendar,
}) => {
  const { trans } = useTrans()
  const { items, count, pending, order, pagination } = events

  useEffect(() => {
    onInit()
  }, [])

  useEffect(() => {
    if (!pending && emptyArray(items) && pagination.page > 1) {
      onChangePage(pagination.page - 1)
    }
  }, [pending, items, pagination.page])

  const handleDuplicate = (params) => Promise.resolve(duplicateEvent(params)).then(() => onInit())

  const [eventModal, setEventModal] = React.useState({ open: false, event: null })
  const openAddModal = () => setEventModal({ open: true, event: null })
  const openEditModal = (event) => setEventModal({ open: true, event })
  const closeEventModal = () => setEventModal((s) => ({ ...s, open: false }))

  const [view, setView] = React.useState('list')
  const isListView = view === 'list'

  const changeView = (next) => {
    if (next === 'list' && view !== 'list') onInit()
    setView(next)
  }

  return (
    <SpacingContainerBase>
      <Typography variant="h2" weight="600" fontFamily="lexend" gutter="bottomSpace">
        {trans('event-management')}
      </Typography>
      {pending && <Loader open={pending} />}
      <Paper>
        <Header onAdd={openAddModal} view={view} onChangeView={changeView} />
        {isListView &&
          (emptyArray(items) ? (
            !pending && <NothingFoundCard description={trans('add-an-event')} />
          ) : (
            <Grid container direction="column">
              <Grid item xs zeroMinWidth>
                <SpacingContainer px="5sm3" py={2}>
                  <List
                    items={items}
                    order={order}
                    onChangeOrder={onChangeOrder}
                    onDelete={deleteEvent}
                    onDuplicate={handleDuplicate}
                    onEdit={openEditModal}
                  />
                </SpacingContainer>
              </Grid>
              <Grid item xs zeroMinWidth>
                <PaginationFooter
                  page={pagination.page}
                  perPage={pagination.perPage}
                  count={count}
                  pending={pending}
                  onChangePage={onChangePage}
                  onChangeRowPerPage={onChangeRowPerPage}
                />
              </Grid>
            </Grid>
          ))}
        {!isListView && <CalendarView onEventClick={openEditModal} />}
      </Paper>
      <EventFormModal
        open={eventModal.open}
        event={eventModal.event}
        onClose={closeEventModal}
        onCreate={createEvent}
        onUpdate={updateEvent}
        onSaved={isListView ? onInit : refetchCalendar}
      />
    </SpacingContainerBase>
  )
}

EventManagement.propTypes = {
  events: PropTypes.shape({
    items: PropTypes.array.isRequired,
    count: PropTypes.number.isRequired,
    pending: PropTypes.bool,
    order: PropTypes.array.isRequired,
    pagination: PropTypes.shape({ page: PropTypes.number, perPage: PropTypes.number }).isRequired,
  }).isRequired,
  onInit: PropTypes.func.isRequired,
  onChangeOrder: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeRowPerPage: PropTypes.func.isRequired,
  deleteEvent: PropTypes.func.isRequired,
  duplicateEvent: PropTypes.func.isRequired,
  createEvent: PropTypes.func.isRequired,
  updateEvent: PropTypes.func.isRequired,
  refetchCalendar: PropTypes.func.isRequired,
}

export default connect(EventManagement)
